// Pure display rules for the repossession tracking screen, kept out of the
// component so the staleness and sort rules are testable.
import type { MapBounds } from '@/components/MapView';
import {
  RentalTrackingDetail,
  RentalTrackingItem,
} from '@/services/api/rentTracking';
import { VehicleTrailPoint } from '@/services/api/vehicleTelemetry';
import {
  ESCALATION_STATUS_COLOR,
  ESCALATION_STATUS_LABEL,
  EscalationStatus,
} from '@/types/rental';
import { toMapsPinUrl } from '@/utils/contact';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Extended here rather than assumed: this module formats in the rental's own
// zone, so it cannot rely on some other file having done it first. dayjs
// ignores a repeated extend.
dayjs.extend(utc);
dayjs.extend(timezone);

/** A fix older than this reads as "we do not really know where it is". */
export const STALE_AFTER_MINUTES = 30;

export type FixFreshness = 'fresh' | 'stale' | 'none';

export const fixFreshness = (item: RentalTrackingItem): FixFreshness => {
  if (!item.position) return 'none';
  const age = item.position.ageMinutes;
  if (age === null || age === undefined) return 'stale';
  return age > STALE_AFTER_MINUTES ? 'stale' : 'fresh';
};

export const formatFixAge = (ageMinutes: number | null): string => {
  if (ageMinutes === null || ageMinutes === undefined) return '-';
  if (ageMinutes < 1) return 'baru saja';
  if (ageMinutes < 60) return `${ageMinutes} mnt lalu`;
  const hours = Math.floor(ageMinutes / 60);
  if (hours < 48) return `${hours} jam lalu`;
  return `${Math.floor(hours / 24)} hari lalu`;
};

export const freshnessToneClass = (freshness: FixFreshness): string => {
  switch (freshness) {
    case 'fresh':
      return 'bg-emerald-50 text-emerald-700';
    case 'stale':
      return 'bg-amber-50 text-amber-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

/** Freshness as ops reads it, for filter chips and legends. */
export const FRESHNESS_LABEL: Record<FixFreshness, string> = {
  fresh: `Fix < ${STALE_AFTER_MINUTES} menit`,
  stale: `Fix > ${STALE_AFTER_MINUTES} menit`,
  none: 'Belum pernah kirim fix',
};

// Harshest rung first, then the biggest debt: the top of the list is the unit
// ops most needs to go and get.
const RUNG_ORDER: Record<string, number> = {
  REPO_REQUESTED: 0,
  SHUTDOWN: 1,
  SHUTDOWN_PENDING: 2,
  WARNED: 3,
  HOLD: 4,
  NORMAL: 5,
};

export const sortTrackingItems = (
  items: RentalTrackingItem[]
): RentalTrackingItem[] =>
  [...items].sort((a, b) => {
    // An open case outranks any rung — it is already a field job.
    const caseDiff = Number(!a.repossession) - Number(!b.repossession);
    if (caseDiff !== 0) return caseDiff;
    const rungDiff =
      (RUNG_ORDER[a.escalationStatus] ?? 9) -
      (RUNG_ORDER[b.escalationStatus] ?? 9);
    if (rungDiff !== 0) return rungDiff;
    return b.arrears.amount - a.arrears.amount;
  });

/** One rung's row in the map legend: what it looks like and how many there are. */
export interface RungTally {
  rung: EscalationStatus;
  label: string;
  color: string;
  units: number;
  /** Units of this rung that have a fix, i.e. that the map can actually show. */
  drawn: number;
}

/**
 * The rungs actually present in the loaded rows, harshest first — the legend's
 * content. Derived from the UNFILTERED rows on purpose: a legend that dropped
 * a rung the moment it was filtered out would leave no way back to it, the
 * same reason trackingFilterOptions reads the unfiltered list.
 */
export const tallyByRung = (items: RentalTrackingItem[]): RungTally[] => {
  const tallies = new Map<string, RungTally>();
  for (const item of items) {
    const rung = item.escalationStatus as EscalationStatus;
    const tally = tallies.get(item.escalationStatus) ?? {
      rung,
      label: ESCALATION_STATUS_LABEL[rung] ?? item.escalationStatus,
      color: rungColor(item.escalationStatus),
      units: 0,
      drawn: 0,
    };
    tally.units++;
    if (item.position) tally.drawn++;
    tallies.set(item.escalationStatus, tally);
  }
  return Array.from(tallies.values()).sort(
    (a, b) => (RUNG_ORDER[a.rung] ?? 9) - (RUNG_ORDER[b.rung] ?? 9)
  );
};

// Telemetry itself lands every few minutes at best and the zone job runs
// hourly, so polling faster than this only re-reads the same snapshot.
export const POLL_INTERVAL_OPTIONS: { label: string; minutes: number }[] = [
  { label: '10 menit', minutes: 10 },
  { label: '30 menit', minutes: 30 },
];

export const DEFAULT_POLL_INTERVAL_MINUTES = POLL_INTERVAL_OPTIONS[0].minutes;

export const summariseTracking = (items: RentalTrackingItem[]) => {
  const summary = {
    total: items.length,
    withCase: 0,
    withFix: 0,
    stale: 0,
    noFix: 0,
    // Age of the newest fix anywhere in the list. When even this is hours old
    // the problem is OEM ingestion, not the units — worth saying on screen.
    freshestFixMinutes: null as number | null,
  };
  for (const item of items) {
    if (item.repossession) summary.withCase++;
    const freshness = fixFreshness(item);
    if (freshness === 'fresh') summary.withFix++;
    else if (freshness === 'stale') summary.stale++;
    else summary.noFix++;
    const age = item.position?.ageMinutes;
    if (
      age !== null &&
      age !== undefined &&
      (summary.freshestFixMinutes === null || age < summary.freshestFixMinutes)
    ) {
      summary.freshestFixMinutes = age;
    }
  }
  return summary;
};

// ─── Search and filters ─────────────────────────────────────────────────

/**
 * The endpoint returns the whole overdue fleet unpaginated, because the map
 * needs every pin anyway, so search and filter run here. Past a few thousand
 * rows this stops being free and belongs back on the server.
 */
export interface TrackingFilters {
  search: string;
  /**
   * Rungs to keep, narrowing the rows already loaded. Separate from the
   * endpoint's own `escalationStatus` parameter, which decides what is
   * fetched: `includeRepossessionCases` drags in units at any rung, so
   * "show me only the ones we have to go and collect" cannot be asked of the
   * API alone. Empty means every rung in the list. Driven by the map legend.
   */
  rungs: EscalationStatus[];
  /** Hub names; TRACKING_NO_VALUE stands for a unit with no hub. */
  hubs: string[];
  oems: string[];
  /** Kabupaten/kota and kecamatan CODES of the last fix, not names. */
  cities: string[];
  districts: string[];
  freshness: FixFreshness[];
  /** true = has an open case, false = has none. Empty means both. */
  hasCase: boolean[];
}

/** Option value for "the row has nothing here" — '' would look like no choice. */
export const TRACKING_NO_VALUE = '__none__';

// "Nothing here" always sorts last, whatever its label happens to be.
const noValueLast = (a: string, b: string): number =>
  Number(a === TRACKING_NO_VALUE) - Number(b === TRACKING_NO_VALUE);

const compareWithNoValueLast = (a: string, b: string): number =>
  noValueLast(a, b) || a.localeCompare(b);

export const EMPTY_TRACKING_FILTERS: TrackingFilters = {
  search: '',
  rungs: [],
  hubs: [],
  oems: [],
  cities: [],
  districts: [],
  freshness: [],
  hasCase: [],
};

/**
 * Filters the user actually picked, for the badge. The rung set arrives
 * pre-selected, so counting it would claim three filters on a fresh page.
 */
export const countChosenFilters = (
  filters: TrackingFilters,
  rungsAreDefault: boolean,
  rungCount: number
): number =>
  (rungsAreDefault ? 0 : rungCount) +
  filters.rungs.length +
  filters.hubs.length +
  filters.oems.length +
  filters.cities.length +
  filters.districts.length +
  filters.freshness.length +
  filters.hasCase.length;

export const hasActiveTrackingFilters = (filters: TrackingFilters): boolean =>
  filters.search.trim() !== '' ||
  filters.rungs.length > 0 ||
  filters.hubs.length > 0 ||
  filters.oems.length > 0 ||
  filters.cities.length > 0 ||
  filters.districts.length > 0 ||
  filters.freshness.length > 0 ||
  filters.hasCase.length > 0;

// Everything ops might type into the box: the plate they were given, the
// mitra, or any of the codes printed on the row.
const searchableFields = (item: RentalTrackingItem): (string | null)[] => [
  item.platNomor,
  item.driverName,
  item.driverCode,
  item.rentalCode,
  item.modelName,
  item.oemName,
  item.hubName,
  item.repossession?.code ?? null,
  item.zone?.cityName ?? null,
  item.zone?.districtName ?? null,
  String(item.driverID),
];

export const matchesTrackingSearch = (
  item: RentalTrackingItem,
  term: string
): boolean => {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  return searchableFields(item).some(
    (field) => field !== null && field.toLowerCase().includes(needle)
  );
};

export interface FilterTrackingOptions {
  /**
   * Leave the area dimension out. The area breakdown uses this so picking one
   * area does not erase the other rows and strand the reader there.
   */
  ignoreAreas?: boolean;
}

export const filterTrackingItems = (
  items: RentalTrackingItem[],
  filters: TrackingFilters,
  options: FilterTrackingOptions = {}
): RentalTrackingItem[] =>
  items.filter((item) => {
    if (!matchesTrackingSearch(item, filters.search)) return false;
    if (
      filters.rungs.length > 0 &&
      !filters.rungs.includes(item.escalationStatus as EscalationStatus)
    ) {
      return false;
    }
    if (
      filters.hubs.length > 0 &&
      !filters.hubs.includes(item.hubName ?? TRACKING_NO_VALUE)
    ) {
      return false;
    }
    if (
      filters.oems.length > 0 &&
      !filters.oems.includes(item.oemName ?? TRACKING_NO_VALUE)
    ) {
      return false;
    }
    if (
      !options.ignoreAreas &&
      filters.cities.length > 0 &&
      !filters.cities.includes(item.zone?.cityCode ?? TRACKING_NO_VALUE)
    ) {
      return false;
    }
    if (
      !options.ignoreAreas &&
      filters.districts.length > 0 &&
      !filters.districts.includes(item.zone?.districtCode ?? TRACKING_NO_VALUE)
    ) {
      return false;
    }
    if (
      filters.freshness.length > 0 &&
      !filters.freshness.includes(fixFreshness(item))
    ) {
      return false;
    }
    if (
      filters.hasCase.length > 0 &&
      !filters.hasCase.includes(item.repossession !== null)
    ) {
      return false;
    }
    return true;
  });

/**
 * What the search box offers while typing: plates first, since that is what
 * ops is handed, then mitra, then areas and codes. Capped because the dropdown
 * is a helper, not a second list.
 */
export const trackingSearchSuggestions = (
  items: RentalTrackingItem[],
  limit = 60
): string[] => {
  const seen = new Set<string>();
  const push = (value: string | null | undefined) => {
    const trimmed = (value ?? '').trim();
    if (trimmed) seen.add(trimmed);
  };
  for (const item of items) push(item.platNomor);
  for (const item of items) push(item.driverName);
  for (const item of items) {
    push(item.zone?.districtName);
    push(item.zone?.cityName);
  }
  for (const item of items) {
    push(item.hubName);
    push(item.driverCode);
    push(item.rentalCode);
    push(item.repossession?.code);
  }
  return Array.from(seen).slice(0, limit);
};

/** An area choice: the code is what filters, the name is what ops reads. */
export interface AreaOption {
  code: string;
  name: string;
}

/**
 * Filter choices read off the loaded rows, so the popup never offers a hub or
 * an area that has no unit on this list. Derive from the unfiltered items or
 * the options vanish as soon as one is picked.
 */
export const trackingFilterOptions = (items: RentalTrackingItem[]) => {
  const hubs = new Set<string>();
  const oems = new Set<string>();
  const cities = new Map<string, string>();
  const districts = new Map<string, string>();
  for (const item of items) {
    hubs.add(item.hubName ?? TRACKING_NO_VALUE);
    oems.add(item.oemName ?? TRACKING_NO_VALUE);
    cities.set(
      item.zone?.cityCode ?? TRACKING_NO_VALUE,
      item.zone?.cityName ?? UNRESOLVED_AREA_LABEL
    );
    districts.set(
      item.zone?.districtCode ?? TRACKING_NO_VALUE,
      item.zone?.districtName ?? UNRESOLVED_AREA_LABEL
    );
  }
  const sortedNames = (values: Set<string>) =>
    Array.from(values).sort(compareWithNoValueLast);
  const sortedAreas = (values: Map<string, string>): AreaOption[] =>
    Array.from(values, ([code, name]) => ({ code, name })).sort(
      (a, b) => noValueLast(a.code, b.code) || a.name.localeCompare(b.name)
    );
  return {
    hubs: sortedNames(hubs),
    oems: sortedNames(oems),
    cities: sortedAreas(cities),
    districts: sortedAreas(districts),
  };
};

// ─── Grouping by area ───────────────────────────────────────────────────

/** Shown wherever a unit's fix has no area yet, in filters and in the list. */
export const UNRESOLVED_AREA_LABEL = 'Belum ada area';

export type AreaLevel = 'city' | 'district';

export interface AreaGroup {
  code: string;
  name: string;
  units: number;
  withCase: number;
  /** Units with a fix, so the group can say how much of it the map shows. */
  drawn: number;
  arrears: number;
  /** The rows themselves, biggest debt first, for the drill-down list. */
  members: RentalTrackingItem[];
}

/**
 * Units per kabupaten/kota or per kecamatan, biggest arrears first — the
 * order ops works the list in. Units whose fix has no area yet group under
 * TRACKING_NO_VALUE rather than disappearing.
 */
export const groupTrackingByArea = (
  items: RentalTrackingItem[],
  level: AreaLevel
): AreaGroup[] => {
  const groups = new Map<string, AreaGroup>();
  for (const item of items) {
    const code =
      (level === 'city' ? item.zone?.cityCode : item.zone?.districtCode) ??
      TRACKING_NO_VALUE;
    const name =
      (level === 'city' ? item.zone?.cityName : item.zone?.districtName) ??
      UNRESOLVED_AREA_LABEL;
    const group = groups.get(code) ?? {
      code,
      name,
      units: 0,
      withCase: 0,
      drawn: 0,
      arrears: 0,
      members: [],
    };
    group.units++;
    if (item.repossession) group.withCase++;
    if (item.position) group.drawn++;
    group.arrears += item.arrears.amount;
    group.members.push(item);
    groups.set(code, group);
  }

  Array.from(groups.values()).forEach((group) => {
    group.members.sort((a, b) => b.arrears.amount - a.arrears.amount);
  });
  return Array.from(groups.values()).sort(
    (a, b) =>
      noValueLast(a.code, b.code) || b.arrears - a.arrears || b.units - a.units
  );
};

// ─── Movement history (trail) ────────────────────────────────────────────────

/** Same caps as basecamp's Big Brother: at most 3 days per fetch, 60 back. */
export const TRAIL_MAX_RANGE_DAYS = 3;
export const TRAIL_MAX_LOOKBACK_DAYS = 60;

export interface TrailRange {
  from: Date;
  to: Date;
}

export const TRAIL_PRESETS: {
  key: string;
  label: string;
  range: () => TrailRange;
}[] = [
  {
    key: 'today',
    label: 'Hari ini',
    range: () => ({ from: dayjs().startOf('day').toDate(), to: new Date() }),
  },
  {
    key: 'yesterday',
    label: 'Kemarin',
    range: () => {
      const today = dayjs().startOf('day');
      return { from: today.subtract(1, 'day').toDate(), to: today.toDate() };
    },
  },
  {
    key: '3d',
    label: '3 hari terakhir',
    range: () => ({
      from: dayjs().startOf('day').subtract(2, 'day').toDate(),
      to: new Date(),
    }),
  },
];

/**
 * Keeps a range fetchable: no future, no more than TRAIL_MAX_LOOKBACK_DAYS
 * back, and at most TRAIL_MAX_RANGE_DAYS wide (the END is kept, the start
 * moves — the recent end is what ops came for). Returns the same instance
 * when nothing had to change so callers can tell.
 */
export const clampTrailRange = (
  range: TrailRange,
  now: Date = new Date()
): TrailRange => {
  let from = dayjs(range.from);
  let to = dayjs(range.to);
  let changed = false;
  if (to.isAfter(now)) {
    to = dayjs(now);
    changed = true;
  }
  const floor = dayjs(now).subtract(TRAIL_MAX_LOOKBACK_DAYS, 'day');
  if (from.isBefore(floor)) {
    from = floor;
    changed = true;
  }
  if (from.isAfter(to)) {
    from = to.subtract(1, 'day');
    changed = true;
  }
  if (to.diff(from, 'day', true) > TRAIL_MAX_RANGE_DAYS) {
    from = to.subtract(TRAIL_MAX_RANGE_DAYS, 'day');
    changed = true;
  }
  return changed ? { from: from.toDate(), to: to.toDate() } : range;
};

/** km/h → colour, same stops as basecamp's Big Brother legend. */
export const SPEED_STOPS: { max: number; color: string; label: string }[] = [
  { max: 5, color: '#3b82f6', label: 'diam / <5' },
  { max: 20, color: '#22c55e', label: '5–20' },
  { max: 40, color: '#84cc16', label: '20–40' },
  { max: 60, color: '#f59e0b', label: '40–60' },
  { max: Infinity, color: '#ef4444', label: '>60 km/j' },
];

export const speedColor = (speedKmh: number): string =>
  (
    SPEED_STOPS.find((s) => speedKmh < s.max) ??
    SPEED_STOPS[SPEED_STOPS.length - 1]
  ).color;

/** [lng, lat] — Mapbox/GeoJSON order, used everywhere on the map side. */
export type LngLat = [number, number];

export interface TrailSegment {
  from: LngLat;
  to: LngLat;
  /** Epoch ms of the segment's END point — what the scrubber crops on. */
  time: number;
  color: string;
}

/**
 * One segment per consecutive point pair, coloured by average speed, so the
 * trail can be cropped by time without rebuilding geometry. Gaps longer than
 * `gapMinutes` are NOT joined: a line across a two-hour blackout would draw
 * a road the unit never took.
 */
export const buildTrailSegments = (
  points: VehicleTrailPoint[],
  gapMinutes = 30
): TrailSegment[] => {
  const segments: TrailSegment[] = [];
  const gapMs = gapMinutes * 60_000;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (b.positioningMs - a.positioningMs > gapMs) continue;
    segments.push({
      from: [a.longitude, a.latitude],
      to: [b.longitude, b.latitude],
      time: b.positioningMs,
      color: speedColor((a.speed + b.speed) / 2),
    });
  }
  return segments;
};

export const trailBounds = (
  points: { latitude: number; longitude: number }[]
): MapBounds | null => {
  if (points.length === 0) return null;
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const p of points) {
    minLat = Math.min(minLat, p.latitude);
    maxLat = Math.max(maxLat, p.latitude);
    minLng = Math.min(minLng, p.longitude);
    maxLng = Math.max(maxLng, p.longitude);
  }
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
};

/**
 * The last point at or before `cursorMs`, or null before the first fix.
 * Binary search: the scrubber fires per pixel over thousands of points.
 */
export const lastPointAtOrBefore = <T extends { positioningMs: number }>(
  points: T[],
  cursorMs: number
): T | null => {
  let lo = 0;
  let hi = points.length - 1;
  let found: T | null = null;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (points[mid].positioningMs <= cursorMs) {
      found = points[mid];
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found;
};

export const summariseTrail = (points: VehicleTrailPoint[]) => {
  let moving = 0;
  let maxSpeed = 0;
  for (const p of points) {
    if (p.movement || p.speed >= 5) moving++;
    if (p.speed > maxSpeed) maxSpeed = p.speed;
  }
  return { points: points.length, moving, maxSpeed };
};

/**
 * Marker colour on the overview map: the rung is what decides the field job.
 * Reads the shared ladder palette so a pin and the same unit's pill in the
 * table cannot drift apart. SHUTDOWN and SHUTDOWN_PENDING used to share one
 * orange here, which erased the difference between "go switch it off" and
 * "already off".
 */
export const rungColor = (escalationStatus: string): string =>
  ESCALATION_STATUS_COLOR[escalationStatus as EscalationStatus] ??
  ESCALATION_STATUS_COLOR[EscalationStatus.NORMAL];

// ─── Unit detail panel ───────────────────────────────────────────────────────

/** Charge types as ops names them; an unmapped type prints its own code. */
export const CHARGE_TYPE_LABEL: Record<string, string> = {
  RENT: 'sewa',
  DEPOSIT_INSTALLMENT: 'cicilan deposit',
  DEPOSIT_DP: 'DP deposit',
};

export const chargeTypeLabel = (type: string): string =>
  CHARGE_TYPE_LABEL[type] ?? type.toLowerCase().replace(/_/g, ' ');

/** "13 Agu – 07 Sep" from two YYYY-MM-DD bounds; one date prints alone. */
export const formatChargeRange = (
  from: string | null,
  to: string | null
): string => {
  if (!from && !to) return '-';
  const fmt = (value: string) => dayjs(value).format('D MMM');
  if (!to || from === to) return fmt(from as string);
  if (!from) return fmt(to);
  return `${fmt(from)} – ${fmt(to)}`;
};

/** One address line from the KTP parts, blanks dropped. */
export const formatAddressParts = (
  address: {
    street: string;
    village?: string;
    district?: string;
    city: string;
    province?: string;
  } | null
): string | null => {
  if (!address) return null;
  return [
    address.street,
    address.village,
    address.district,
    address.city,
    address.province,
  ]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(', ');
};

// ─── Copyable summary ───────────────────────────────────────────────────

/** Indonesia's three civil zones, for a timestamp ops will paste into chat. */
const TZ_LABEL: Record<string, string> = {
  'Asia/Jakarta': 'WIB',
  'Asia/Makassar': 'WITA',
  'Asia/Jayapura': 'WIT',
};

const zoneLabel = (timezone: string): string => TZ_LABEL[timezone] ?? timezone;

/** "2026-09-07 21:46:41 WIB" — unambiguous once it leaves the screen. */
const stampInZone = (iso: string, timezone: string): string =>
  `${dayjs(iso).tz(timezone).format('YYYY-MM-DD HH:mm:ss')} ${zoneLabel(
    timezone
  )}`;

const line = (label: string, value: string | null | undefined): string =>
  `- ${label}: ${value && value.trim() ? value.trim() : '-'}`;

/**
 * The unit as plain text, for pasting into a chat with whoever goes to get it.
 *
 * Deliberately carries NO money — not the arrears amount, the daily price, the
 * deposit or the prepaid balance. Only the number of days, because that is what
 * decides whether someone drives out, and because this text leaves the tool and
 * lands in group chats.
 */
export const buildUnitClipboardText = (
  item: RentalTrackingItem,
  detail: RentalTrackingDetail | null
): string => {
  const timezone = detail?.rental.timezone ?? 'Asia/Jakarta';
  const position = detail?.vehicle.position ?? item.position;
  const area = [item.zone?.districtName, item.zone?.cityName]
    .filter(Boolean)
    .join(', ');

  const blocks: string[] = [];

  blocks.push(
    [
      'Informasi motor:',
      line('Plat', item.platNomor),
      line(
        'OEM / model',
        [item.oemName, item.modelName].filter(Boolean).join(' · ')
      ),
      line('VIN', detail?.vehicle.vin),
      line(
        'Hub',
        [item.hubName, detail?.rental.hubCityName].filter(Boolean).join(' · ')
      ),
    ].join('\n')
  );

  blocks.push(
    [
      'Informasi peminjam:',
      line('Nama', detail?.driver.name ?? item.driverName),
      line('Kode mitra', detail?.driver.code ?? item.driverCode),
      line('Telepon', detail?.driver.phoneNumber),
      line('Alamat', formatAddressParts(detail?.driver.address ?? null)),
    ].join('\n')
  );

  const contacts = detail?.driver.emergencyContacts ?? [];
  blocks.push(
    [
      'Kontak darurat:',
      ...(contacts.length > 0
        ? contacts.map((contact) =>
            [
              `- ${contact.name} (${contact.relationship})`,
              contact.phoneNumber,
              [contact.address, contact.cityName].filter(Boolean).join(', '),
            ]
              .filter(Boolean)
              .join(' · ')
          )
        : ['- Tidak ada kontak darurat pada data KYC mitra.']),
    ].join('\n')
  );

  blocks.push(
    [
      'Informasi rental:',
      line('Kode rental', item.rentalCode),
      line(
        'Status',
        ESCALATION_STATUS_LABEL[item.escalationStatus as EscalationStatus] ??
          item.escalationStatus
      ),
      line('Menunggak', `${item.arrears.days} hari`),
      line(
        'Rentang tunggakan',
        detail
          ? formatChargeRange(
              detail.arrears.oldestChargeDate,
              detail.arrears.newestChargeDate
            )
          : null
      ),
      line('Case repo', item.repossession?.code),
    ].join('\n')
  );

  const lastSeen = position
    ? [
        area || null,
        position.lastAt ? stampInZone(position.lastAt, timezone) : null,
        `${position.latitude}, ${position.longitude}`,
        toMapsPinUrl(position.latitude, position.longitude),
      ]
        .filter(Boolean)
        .join(' · ')
    : 'unit ini belum pernah mengirim koordinat';

  return `${blocks.join('\n\n')}\n\nLokasi terakhir: ${lastSeen}\n`;
};
